// One confirmation dialog for the whole app.
//
// Ported from Zanetto Tools (phase 7 stage 2). It replaces `window.confirm()`,
// which could not be styled, could not show which button was the destructive
// one, was titled with the hostname, and on Windows put default focus on OK —
// so a stray Enter deleted things. It also meant the app had two visual
// languages for "are you sure", depending on what you happened to be deleting.
//
// Deliberately promise-based so call sites stay a one-liner:
//
//     if (!(await confirm({ title: 'Delete this booking?', … }))) return;
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

export interface ConfirmOptions {
  /** Short question, ends in "?". */
  title: string;
  /** One or two sentences of consequence. */
  message?: ReactNode;
  /** Repeats the trigger's verb — never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' is irreversible only; 'warning' is reversible but notable. */
  tone?: 'danger' | 'warning' | 'primary';
  /**
   * When set, the exact string must be typed before confirm enables. For
   * deletes that take more than one record with them — in Dayboard that is
   * releasing a job's whole schedule, and tenant deletion.
   */
  requireText?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

const TONE_CLASS = {
  danger: 'btn-danger btn-sm',
  warning: 'btn-warning btn-sm',
  primary: 'btn-primary btn-sm',
} as const;

interface Request {
  options: ConfirmOptions;
  resolve: (result: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [typed, setTyped] = useState('');

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setTyped('');
        setRequest({ options, resolve });
      }),
    []
  );

  const settle = (result: boolean) => {
    if (request) request.resolve(result);
    setRequest(null);
    setTyped('');
  };

  const opts = request?.options;
  const needsTyping = Boolean(opts?.requireText);
  const canConfirm = !needsTyping || typed.trim() === opts?.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal
          title={opts.title}
          onClose={() => settle(false)}
          footer={
            <>
              <button type="button" onClick={() => settle(false)} className="btn-ghost btn-sm">
                {opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => settle(true)}
                disabled={!canConfirm}
                className={TONE_CLASS[opts.tone ?? 'primary']}
              >
                {opts.confirmLabel ?? 'Confirm'}
              </button>
            </>
          }
        >
          {opts.message && <div className="text-sm text-gray-600">{opts.message}</div>}
          {needsTyping && (
            <div className="mt-4">
              <label className="form-label" htmlFor="confirm-phrase">
                Type{' '}
                <span className="font-mono font-semibold text-gray-900">{opts.requireText}</span> to
                confirm
              </label>
              <input
                id="confirm-phrase"
                type="text"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                className="form-input"
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}
